import { apiService } from './api';

export interface PriceTrigger {
  id: string;
  user_id: string;
  symbol: string;
  stock_name?: string;
  trigger_price: number;
  trigger_type: 'higher' | 'lower';
  is_active: boolean;
  is_triggered: boolean;
  triggered_at?: string;
  triggered_price?: number;
  current_price?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTriggerRequest {
  symbol: string;
  trigger_price: number;
  trigger_type: 'higher' | 'lower';
}

export interface UpdateTriggerRequest {
  symbol: string;
  trigger_price: number;
  trigger_type: 'higher' | 'lower';
}

export interface PriceTriggerStats {
  total_triggers: number;
  active_triggers: number;
  triggered_count: number;
  inactive_triggers: number;
}

export interface PriceTriggersResponse {
  success: boolean;
  triggers: PriceTrigger[];
  count: number;
}

export interface CreateTriggerResponse {
  success: boolean;
  message: string;
  trigger: PriceTrigger;
}

export interface DeleteTriggerResponse {
  success: boolean;
  message: string;
}

export interface BulkDeleteResponse {
  success: boolean;
  message: string;
  deleted_count: number;
}

export interface TriggerStatsResponse {
  success: boolean;
  stats: PriceTriggerStats;
}

class PriceTriggersService {
  /**
   * Get all price triggers for the current user
   */
  async getPriceTriggers(): Promise<PriceTriggersResponse> {
    return await apiService.get<PriceTriggersResponse>('/price-triggers');
  }

  /**
   * Create a new price trigger
   */
  async createPriceTrigger(data: CreateTriggerRequest): Promise<CreateTriggerResponse> {
    return await apiService.post<CreateTriggerResponse>('/price-triggers', data);
  }

  /**
   * Update an existing price trigger
   */
  async updatePriceTrigger(triggerId: string, data: UpdateTriggerRequest): Promise<CreateTriggerResponse> {
    return await apiService.put<CreateTriggerResponse>(`/price-triggers/${triggerId}`, data);
  }

  /**
   * Delete a specific price trigger
   */
  async deletePriceTrigger(triggerId: string): Promise<DeleteTriggerResponse> {
    return await apiService.delete<DeleteTriggerResponse>(`/price-triggers/${triggerId}`);
  }

  /**
   * Delete multiple price triggers
   */
  async bulkDeletePriceTriggers(triggerIds: string[]): Promise<BulkDeleteResponse> {
    return await apiService.deleteWithData<BulkDeleteResponse>('/price-triggers/bulk-delete', {
      trigger_ids: triggerIds
    });
  }

  /**
   * Toggle a price trigger active/inactive status
   */
  async togglePriceTrigger(triggerId: string): Promise<CreateTriggerResponse> {
    return await apiService.put<CreateTriggerResponse>(`/price-triggers/${triggerId}/toggle`);
  }

  /**
   * Get statistics about user's price triggers
   */
  async getPriceTriggerStats(): Promise<TriggerStatsResponse> {
    return await apiService.get<TriggerStatsResponse>('/price-triggers/stats');
  }
}

export const priceTriggersService = new PriceTriggersService();
export default priceTriggersService;
